import React from 'react';
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { IUserInfoModel } from '../models/IOpportunitiesAPIModel';
import { IAOPTargetsStatusItemModel, IKPIModel } from '../models/IUserProfileModel';
import axios from '../utils/APIUtils';
import { DASHBOARD_TABS, OPPORTUNITIES_API_PATHS, PROFILES_API_PATHS, getRealizationToastMsg } from '../constants/constants';
import { GenericState } from './ISliceInterfaces';
import { toast } from 'react-toastify';
import { getTargetImpact } from '../utils/dashboardUtilis';
import { Snackbar } from '../components/Snackbar/Snackbar';

/** Contains user configurations */
export interface IUserConfig {
  activeDashboardTab: DASHBOARD_TABS; // indicates active tab index
}

export const initialState = {
  userInfo: {} as IUserInfoModel,
  aopTargets: { status: 'idle' } as GenericState<IAOPTargetsStatusItemModel[]>,
  config: { activeDashboardTab: DASHBOARD_TABS.ALL_OPPORTUNITIES } as IUserConfig,
  kpis: { status: 'idle' } as GenericState<IKPIModel[]>,
};

export interface IgetAopTargetStatusArgs {
  showToast: boolean;
  toastType: 'accept' | 'realize' | 'reject';
  kpiId?: number;
}

export const getAopTargetsStatus = createAsyncThunk('userProfile/getAopTargetsStatus', 
  async (args?: IgetAopTargetStatusArgs) => {
  const response = await axios.get<IAOPTargetsStatusItemModel[]>(OPPORTUNITIES_API_PATHS.aopValues);
  if (args && args.showToast && args.toastType === 'realize') {
    const kpiMatch = response.data.find(aopTargetItem => aopTargetItem.kpi_id === args.kpiId);
    if (kpiMatch) {
      const currentPercentage = getTargetImpact (kpiMatch.kpi_value, (Number(kpiMatch.realized_value)))
      toast(<Snackbar toastMessage={getRealizationToastMsg(currentPercentage, kpiMatch.kpi_name)} />, { position: 'bottom-right', className: 'fi-toast--snackbar', bodyClassName: 'fi-toast__body--snackbar', closeOnClick: true });
    }
  }
  return response.data;
});

/** Fetches user KPI informations and kpi ui config */
export const getUserKpis = createAsyncThunk('userProfile/kpis', async () => {
  const response = await axios.get<IKPIModel[]>(PROFILES_API_PATHS.kpis);
  return response.data;
})

export const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState,
  reducers: {
    setUserInfo: (state, action: PayloadAction<IUserInfoModel>) => {
      state.userInfo = action.payload;
      return state;
    },
    setAopTargetsStatus: (state, action: PayloadAction<IAOPTargetsStatusItemModel[]>) => {
      state.aopTargets.data = action.payload;
    },
    setActiveDashboardTab: (state, action: PayloadAction<number>) => {
      if (Object.values(DASHBOARD_TABS).includes(action.payload)) {
        state.config.activeDashboardTab = action.payload;
      }
    }
  },
  extraReducers: builder => {
      builder.addCase(getAopTargetsStatus.pending, (state) => {
        state.aopTargets.status = 'loading';
      });
      builder.addCase(getAopTargetsStatus.fulfilled, (state, action) => {
        state.aopTargets.status = 'finished';
        state.aopTargets.data = action.payload;
      });
      builder.addCase(getAopTargetsStatus.rejected, (state) => {
        state.aopTargets.status = 'error';
      });
      builder.addCase(getUserKpis.pending, (state) => {
        state.kpis.status = 'loading';
      });
      builder.addCase(getUserKpis.fulfilled, (state, action) => {
        state.kpis.status = 'finished';
        state.kpis.data = action.payload;
      });
      builder.addCase(getUserKpis.rejected, (state) => {
        state.kpis.status = 'error';
      });
  }
});

export const { setUserInfo, setAopTargetsStatus, setActiveDashboardTab } = userProfileSlice.actions;

export const getUserInfo = (state: RootState) => state.userProfile.userInfo;

export const getAopTargetsAPIStatus = (state: RootState) => state.userProfile.aopTargets.status;

export const getAopTargets = (state: RootState) => state.userProfile.aopTargets.data;
/** To get active dashboard tab index */
export const getActiveDashboardTab = (state: RootState) => state.userProfile.config.activeDashboardTab;

/** Get user KPI API details selector */
export const getKPIDetails = (state: RootState) => state.userProfile.kpis;

export const selectCurrentUser = (state: RootState) => state.userProfile.userInfo.email;

export default userProfileSlice.reducer;